/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useState } from 'react';
import { useAppDispatch } from '../../reducers';
import { getGroup } from '@g17eco/core';
import G17Client from '../../services/G17Client';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { DownloadCard as ShareDownloadCard } from '@g17eco/molecules/download-card';
import { DownLoadCardItem, getDataShareMultiScopeReport } from './downloadCards';
import { SurveyListItem } from '../../types/survey';
import { getDownloadMultiScope } from '@features/downloads/util/custom';
import { DownloadButton } from '@g17eco/molecules/button/DownloadButton';
import { DownloadType, VisibilityStatus } from '@g17eco/types/download';
import { DataShareReportDownloadButton, DownloadButtonProps } from './DataShareReportDownloadButton';
import { getDefaultDownloadScope, isSupportedReportCode } from '@features/report-output/outputs';
import { DownloadSettingModal } from '../downloads/DownloadSettingModal';
import { SURVEY } from '@constants/terminology';


interface Props extends DownloadButtonProps {
  surveyListItem: SurveyListItem;
  items: DownLoadCardItem[];
}

type SupportedDownloadType = DownloadType.Csv | DownloadType.Xlsx;
enum DownloadCode {
  All = 'all',
}

const getCardProps = (primaryCode: string) => {

  if (primaryCode !== 'sgx_metrics') {
    return {
      item: {
        code: primaryCode,
        name: 'Sustainability report',
        src: undefined,
        icon: 'fa-thin fa-seedling text-ThemeSuccessMedium',
      }
    }
  }

  return {
    item: {
      code: primaryCode,
      name: 'Sustainability Report',
      src: getGroup('standards', primaryCode)?.src,
    },
    titleOptions: {
      word: {
        title: 'Sustainability Word report',
        tooltip: 'SGX and RegCo compliant Word report'
      }
    }
  };
};

export const DataShareDownloads = (props: Props) => {

  const {
    initiativeId,
    requesterId,
    requesterType,
    surveyListItem,
    primaryCode,
    items,
  } = props;

  const surveyId = surveyListItem._id;
  const dispatch = useAppDispatch();
  const analytics = getAnalytics();

  const setMessage = (message: string) => {
    dispatch(addSiteAlert({ content: message, color: SiteAlertColors.Danger }));
  }

  const [openItem, setOpenItem] = useState<DownLoadCardItem | undefined>(undefined);
  const track = (type: string, code?: string) => {
    analytics.track(AnalyticsEvents.SurveyDataDownloaded, {
      initiativeId,
      surveyId: surveyId,
      source: 'data_share_download',
      type: type,
      scopeValue: code,
    })
  };

  const downloadDocx = async (code: string) => {

    if (!isSupportedReportCode(code)) {
      setMessage(`Not supported report type "${code}"`)
      return;
    }

    track('docx', code);
    const downloadScope = getDefaultDownloadScope([code], {
      visibility: VisibilityStatus.ExcludeValuesOnly,
      targets: false,
    });
    return getDataShareMultiScopeReport({
      requesterId,
      requesterType,
      initiativeId,
      surveyId,
      downloadScope,
      code,
    }).catch((e) => {
      console.error(e)
      setMessage(e.message)
    });
  }

  const allDataDownload = async (type: SupportedDownloadType, code: DownloadCode.All | string) => {
    const simpleType = DownloadType.Csv === type ? 'csv' : 'xlsx';
    track(simpleType, code);

    return G17Client.downloadDataShareSurveySimple({
      surveyId,
      type: simpleType,
      requesterId,
      requesterType,
      initiativeId,
      downloadScope: DownloadCode.All === code ? undefined : { scope: getDownloadMultiScope(code) },
    }).catch((e) => setMessage(e.message));
  }

  const hasSurvey = !!surveyListItem;
  const { item: mainItem, titleOptions } = getCardProps(primaryCode);

  const getOnDownload = async (type: string, code: string) => {

    if (code === 'sdg' && type === DownloadType.Csv) {
      track('csv', code);
      return G17Client.requesterDownloadSDG({
        surveyId,
        requesterId,
        requesterType,
        initiativeId,
      }).catch((e) => setMessage(e.message));
    }

    switch (type) {
      case DownloadType.Docx:
        return downloadDocx(code);
      case DownloadType.Csv:
      case DownloadType.Xlsx:
        return allDataDownload(type, code);
      default:
        setMessage('Not supported download type')
        return;
    }
  };

  return (
    <>
      <div className='download-container border-0 pb-3 px-4'>
        <div className='mx-2 pt-2 d-flex align-items-start flex-wrap'>
          <ShareDownloadCard aside={true} item={mainItem}>
            <DataShareReportDownloadButton titleOptions={titleOptions} {...props} />
          </ShareDownloadCard>
          <div className='download-card'>
            <section className='download-details shadow-none border-0 d-flex flex-column'>
              <div className='download-actions d-flex flex-column mt-0'>
                <div className={'strong download-card__heading mb-3'}>Download all {SURVEY.SINGULAR} data</div>
                <DownloadButton
                  disabled={!hasSurvey}
                  block
                  color='link'
                  onClick={() => allDataDownload(DownloadType.Xlsx, DownloadCode.All)}
                >
                  <i className='fas fa-file-excel mr-2 fa-lg' />
                  Download all data to Excel
                </DownloadButton>
                <DownloadButton
                  disabled={!hasSurvey}
                  block
                  color='link'
                  onClick={() => allDataDownload(DownloadType.Csv, DownloadCode.All)}
                >
                  <i className='fas fa-file-csv mr-2 fa-lg' />
                  Download all data to CSV
                </DownloadButton>
              </div>
            </section>
          </div>
        </div>
      </div>
      <h4 className='mt-5 mb-3'>Standards, Frameworks & Policies</h4>
      <div className='download-container py-3 px-4'>
        <div className='mt-3 mx-2 d-flex flex-wrap justify-content-between mb-5'>
          {items.map((item) => {
            return item.isVisible ? (
              <ShareDownloadCard key={item.code} aside={true} item={item}>
                <DownloadButton
                  className='doc-btn'
                  color='secondary'
                  outline={true}
                  disabled={item.isDisabled}
                  onClick={() => setOpenItem(item)}
                >
                  Download report <i className='fas fa-file-download ml-2' />
                </DownloadButton>
              </ShareDownloadCard>
            ) : null;
          })}
        </div>
        {openItem ? (
          <DownloadSettingModal
            title={openItem.name}
            code={openItem.code}
            portfolioId={requesterId}
            disabledOptions={['detailed']}
            handleClose={() => setOpenItem(undefined)}
            onDownload={(type, config) => getOnDownload(type, openItem.code ?? config?.packCode)}
            isOpen={true}
          />
        ) : null}
      </div>
    </>
  );
}

